import { Check } from "lucide-react";
import { useRef, useState, type FormEvent } from "react";
import { isPlayerAvatarId, PLAYER_AVATAR_IDS, type PublicPlayer } from "@ma-soi/game-core";
import { errorMessage } from "../lib";
import { GameButton } from "./GothicLobby";
import { SeatAvatar, seatAvatarSrc } from "./SeatAvatar";
import { BottomSheet } from "./ui/BottomSheet";

export function AvatarPicker({ player, connected, onSave, onClose }: {
  player: PublicPlayer;
  connected: boolean;
  onSave: (avatarId: number) => Promise<unknown>;
  onClose: () => void;
}) {
  const currentId = isPlayerAvatarId(player.avatarId) ? player.avatarId : isPlayerAvatarId(player.seat) ? player.seat : null;
  const [selectedId, setSelectedId] = useState<number | null>(currentId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitting = useRef(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (submitting.current || !connected || selectedId === null || selectedId === currentId) return;
    submitting.current = true;
    setSaving(true);
    setError(null);
    try {
      await onSave(selectedId);
      onClose();
    } catch (reason) {
      // Giữ lựa chọn khi gửi lỗi để người chơi có thể thử lưu lại.
      setError(errorMessage(reason));
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  }

  return (
    <BottomSheet open title="Đổi ảnh đại diện" onClose={onClose} className="avatar-picker-dialog">
      <form className="avatar-picker" onSubmit={(event) => void save(event)} aria-busy={saving}>
        <div className="avatar-picker-content">
          <div className="avatar-picker-preview">
            <SeatAvatar seat={player.seat} avatarId={selectedId ?? undefined} name={player.name} />
            <div><strong>{player.name}</strong><p>Mọi người trong phòng sẽ thấy avatar này sau khi bạn lưu.</p></div>
          </div>
          <fieldset className="avatar-picker-fieldset" disabled={saving}>
            <legend>Chọn một trong 16 chân dung có sẵn</legend>
            <div className="avatar-picker-grid">
              {PLAYER_AVATAR_IDS.map((id) => (
                <label key={id} className="avatar-option">
                  <input type="radio" name="avatar" value={id} checked={selectedId === id} onChange={() => { setSelectedId(id); setError(null); }} />
                  <span className="avatar-option-art"><img src={seatAvatarSrc(id)!} alt="" width="384" height="384" draggable={false} />{selectedId === id && <span className="avatar-option-check"><Check aria-hidden="true" /></span>}</span>
                  <span className="avatar-option-label"><span className="sr-only">Chân dung </span>{String(id).padStart(2, "0")}</span>
                </label>
              ))}
            </div>
          </fieldset>
        </div>
        <div className="avatar-picker-footer">
          {!connected && <p role="status">Đang kết nối lại… Bạn có thể lưu khi kết nối trở lại.</p>}
          {error && <p className="error-box" role="alert">{error}</p>}
          <div className="avatar-picker-actions">
            <GameButton type="button" variant="secondary" onClick={onClose}>Hủy</GameButton>
            <GameButton type="submit" variant="gold" disabled={saving || !connected || selectedId === null || selectedId === currentId}>{saving ? "Đang lưu…" : "Lưu avatar"}</GameButton>
          </div>
        </div>
      </form>
    </BottomSheet>
  );
}
